/* eslint-disable max-classes-per-file */
import { MessageKind } from 'app/services/messaging/message';
import { MessagingService, MessagingServiceChannel } from '../../services/messaging/messaging-service';
import { Application } from 'app/application';
import { IModel, Collection, IViolation } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { Named } from '../base/named';
import { DataFlow } from 'app/model/dataflow/dataflow';
import { DataFlowColumn } from 'app/model/dataset/dataflowcolumn';

import { OwnedSet } from 'shell-models-lib';
import { Owned } from 'app/model/base/named';
import { ColumnReference } from 'app/model/dataset/columnReference';
import { stringsAreEqualCaseInsensitive, format, sanitizeAndFormatString } from 'app/shared/utils/string.util';
import { DataFlowExpression } from './dataflowexpression';
import { ExpressionBasedColumn } from './expression-based.column';
import { ExpressionBasedColumnBase } from './expression-based.column.base';
import { ExpressionBasedColumnPattern } from './expression-based.column.pattern';
import {
    IDataFlowArguments, convertColumnRefToDataFlowColumn, dataFlowPatternParameter,
    generateGrammarForColumns, getSortedExpressionsInColumnArguments, IUsedIdentifier, DataFlowColumnArgument
} from './dataflow-expression-utilities';
import { Message } from '../../services/messaging/message';
import { Constants } from 'app/shared/shared-constants';
import { UUID } from '../services/uuid';
import { asyncUpdateIdentifiers, updateIdentifiers } from './dataflow-expression-update-identifiers';
import { IExtraFieldInfo } from 'app/interfaces/IExtraFieldInfo';
import { DataFlowTypesUtils } from './dataflow-type-utilities';
import { DataFlowExpressionBasedMapColumn } from './dataflow-expression-based-map.column';
import { Parameter } from '../base/parameter';
import { BasicExpression } from './expressionTypes';
import { StringDataFlowType, IntegerDataFlowType, ArrayDataFlowType } from './dataflow-datatype';
import {
    PatternExpressionColumnsUtils
} from 'app/authoring/component/transformations/pattern-expression-columns-utils';
import { GlobalVariables } from 'app/services/globals';
import { ColumnExpressionProcessor } from './column-expression-processor';
import { ComplexDataFlowType } from './dataflow-complex-datatype';
import { DataFlowSchemaAdapterUtils } from '../adapters/dataflow/dataflow-schema-adapter-utils';
import { DataFlowSink } from './dataflow-sink';
import { dataFlowPositionParameter, dataFlowHierarchyPathParameter } from 'worker/app-workers/shared/constants';
import { DataFlowUtils } from 'app/authoring/component/transformations/dataFlow-utils';
import { DataFlowExpressionArray } from 'app/model/dataflow/dataflow-expression-array';
import { IResourceContext } from 'app/multi-contexts/resource-context.typings';

export type PartitionOption = 'Default' | 'NoPartition' | 'Partitioning';
export type PartitionMethod = 'roundRobin' | 'hash' | 'dynamicRange' | 'fixed' | 'key' | 'external';

export interface ITransformationPosition {
    row: number;
    column: number;
}

export interface ITranformationUIData {
    visited: boolean;
    position: ITransformationPosition;
    /*
        Indicate whether the node is clicked to collapse or not.
    */
    collapsed: boolean;
}

export enum DebugState {
    Valid = 'valid',
    Invalid = 'invalid',
    InvalidStream = 'invalidstream'
}

@Metamodel.model({
    name: 'dataflowstream',
    referable: true
})
export class DataFlowStream extends Named {
    public static readonly MODEL: IModel;

    public get producer(): Transformation {
        return this.owner as Transformation;
    }

    public get qualifiedName(): string {
        return !this.name ? this.producer.name : `${this.producer.name}@${this.name}`;
    }

    @Metamodel.attribute({
        reference: true,
        type: DataFlowStream.consumerModel,
        collection: Collection.Array
    })
    public readonly consumers: Transformation[] = [];

    private static consumerModel(): IModel {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        return Transformation.MODEL;
    }

    public removeConsumer(consumer: Transformation): void {
        const index = this.consumers.findIndex((item) => item === consumer);
        this.consumers.splice(index, 1);
    }

    public getAllDownStreams(visitedTransformations: Set<Transformation> = new Set<Transformation>()): DataFlowStream[] {
        const downStreams: DataFlowStream[] = [];
        if (this.consumers.length === 0) {
            return downStreams;
        }
        this.consumers.forEach((consumer) => {
            if (!visitedTransformations.has(consumer)) {
                visitedTransformations.add(consumer);
                consumer.outputStreams.forEach((stream) => {
                    downStreams.push(stream);
                    downStreams.push(...stream.getAllDownStreams(visitedTransformations));
                });
            }
        });

        return downStreams;
    }
}

@Metamodel.model({
    name: 'transformation', // As this is abstract this will never be used
    display: ['transformation', 'transformations'],
    referable: true,
    abstract: true,
    constraints: {
        name: {
            required: true,
            validator: Transformation.validateNameAlphanumeric
        }
    },
    tags: {
        isTransformation: true
    }
})
export abstract class Transformation extends Named {
    public static readonly MODEL: IModel;
    public static readonly PARTITIONING_CONFIG_MIN_PARTITIONS: number = 2;

    public uiData: ITranformationUIData = {
        visited: false,
        position: {
            row: 0,
            column: 0
        },
        collapsed: false
    };

    @Metamodel.attribute({
        display: stringResources.OutputStreamLabel,
        collection: Collection.Set,
        type: Transformation.streamModel
    })
    public outputStreams = OwnedSet.create<DataFlowStream>(this);

    @Metamodel.attribute({
        transient: true
    })
    public get visibleOutputStreams(): DataFlowStream[] {
        return Array.from(this.outputStreams);
    }

    @Metamodel.attribute({
        transient: true
    })
    public inputCacheId: string = UUID.uuid4();

    @Metamodel.attribute({
        transient: true
    })
    public outputCacheId: string = UUID.uuid4();

    @Metamodel.attribute({
        display: stringResources.InputStreamLabel,
        collection: Collection.Array,
        type: Transformation.streamModel,
        reference: true
    })
    public inputStreams: DataFlowStream[] = [];

    public inputStreamNames: string[] = [];
    public hasHierarchicalData: boolean;
    public partitionOption: PartitionOption = 'Default';
    public partitionMethod: PartitionMethod = 'roundRobin';

    @Metamodel.attribute({
        display: stringResources.ModelName_Partition_Option,
        constraint: {
            validator: Transformation.validatePartitionsNumber
        }
    })
    public numberOfPartitions: DataFlowExpression = this.createTemplatableExpression(IntegerDataFlowType.TYPE, '1');

    @Metamodel.attribute({
        display: stringResources.ModelName_Partition_Option,
        constraint: {
            validator: Transformation.validatePartitionConditions
        }
    })
    public partitionConditions: DataFlowExpression[] = [];

    @Metamodel.attribute({
        transient: true
    })
    public lastDataFlowRunId: string;

    public inspectSummaryOutputFields: IExtraFieldInfo[] = [];
    public inspectDetailsOutputFields: IExtraFieldInfo[] = [];
    public inspectSummaryInputFields: IExtraFieldInfo[] = [];
    public inspectDetailsInputFields: IExtraFieldInfo[] = [];
    public showInspectInput: boolean = false;
    public isPowerQueryTransformation: boolean = false;
    public unknownProps: string[] = [];

    public get owner(): DataFlow {
        return (super.getOwner() as DataFlow);
    }

    public get columns(): DataFlowColumn[] {
        return [];
    }

    // TODO mablumen: In some cases with DataFlowSink, owner is PowerQuerySink > ExecuteWranglingDataFlowActivity
    // Checking for instanceof will cause circular dependency issues. Need to rethink this flow.
    public get dataflow(): DataFlow {
        return this.owner;
    }

    public get cachedColumns(): DataFlowColumn[] {
        if (!this._cachedColumns) {
            this._cachedColumns = this.columns;
        }
        return this._cachedColumns;
    }

    public get transientColumns(): DataFlowColumn[] {
        return ColumnExpressionProcessor.processNewColumns(this.transientExpressionColumns, this.distinctInputColumnRefs, this)
            .map((processedColumnResult) => processedColumnResult.column);
    }

    @Metamodel.attribute({
        transient: true
    })
    public debugState: DebugState = DebugState.Valid;

    public transientExpressionColumns: ExpressionBasedColumnBase[] = [];

    @Metamodel.attribute({
        transient: true
    })
    private _cachedDistinctInputColumns: ColumnReference[] = undefined;

    @Metamodel.attribute({
        transient: true
    })
    private _internalCachedDistinctInputColumns: ColumnReference[] = undefined;

    @Metamodel.attribute({
        transient: true
    })
    private _cachedDistinctOutputColumns: ColumnReference[] = undefined;

    @Metamodel.attribute({
        transient: true
    })
    private _internalCachedDistinctOutputColumns: ColumnReference[] = undefined;

    @Metamodel.attribute({
        transient: true
    })
    private _cachedOutputColumns: Map<DataFlowStream, DataFlowColumn[]> = new Map<DataFlowStream, DataFlowColumn[]>();

    @Metamodel.attribute({
        transient: true
    })
    private _cachedColumns: DataFlowColumn[] = undefined;

    @Metamodel.attribute({
        transient: true
    })
    private _cachedDescription: string = undefined;

    public static validateTemplateExpressions(transformation: Transformation, expressions: DataFlowExpression[], attributeName: string): IViolation[] {
        const violations: IViolation[] = [];
        expressions.forEach((expression) => {
            if (expression.templateExpression && !expression.returnsCorrectType) {
                violations.push({
                    context: transformation,
                    message: () => format(stringResources.DataFlowExpressionUnexpectedReturnType, `'${attributeName}'`, expression.requiredReturnType)
                });
            }
        });
        return violations;
    }

    public static validatePartitionsNumber(transformation: Transformation): IViolation[] | undefined {
        const violations: IViolation[] = [];
        // If it is not an expression, validate the partition numbers, otherwise, validate the expression.
        if (!transformation.numberOfPartitions.templateExpression) {
            if (transformation.partitionOption === 'Partitioning' && transformation.partitionMethod !== 'key' && transformation.partitionMethod !== 'external') {
                const numberOfPartitions = parseInt(transformation.numberOfPartitions.expression, 10);
                if (isNaN(numberOfPartitions) || numberOfPartitions < Transformation.PARTITIONING_CONFIG_MIN_PARTITIONS) {
                    violations.push({
                        context: transformation,
                        message: () => sanitizeAndFormatString(stringResources.DataFlowTransformationNumberOfPartitionsError, transformation.name)
                    });
                }
            }
        } else {
            violations.push(...Transformation.validateTemplateExpressions(transformation, [transformation.numberOfPartitions], stringResources.ModelName_Partition_Option));
        }

        return violations;
    }

    public static validatePartitionConditions(transformation: Transformation): IViolation[] | undefined {
        const violations: IViolation[] = [];
        if (transformation.partitionOption === 'Partitioning') {
            if (transformation.partitionMethod === 'fixed') {
                if (!transformation.numberOfPartitions.templateExpression) {
                    const numberOfPartitions = parseInt(transformation.numberOfPartitions.expression, 10);
                    if (isNaN(numberOfPartitions) || numberOfPartitions < transformation.partitionConditions.length
                        || numberOfPartitions - 1 > transformation.partitionConditions.length) {
                        violations.push({
                            context: transformation,
                            message: () => sanitizeAndFormatString(stringResources.DataFlowTransformationNumberOfPartitionConditionsError,
                                `${(numberOfPartitions - 1)}`, `${(numberOfPartitions)}`, transformation.name)
                        });
                    }
                }
                if (transformation.partitionConditions.length
                    && transformation.partitionConditions.find((condition) => condition.expression && condition.returnType.typeString !== 'boolean')) {
                    violations.push({
                        context: transformation,
                        message: () => sanitizeAndFormatString(stringResources.DataFlowTransformationPartitionConditionsTypeError, 'boolean', transformation.name)
                    });
                }
            }
        }

        return violations;
    }

    public static validateNameAlphanumeric(transformation: Transformation): IViolation[] | undefined {
        // Wrangling dataflow transformations must share the same name as the corresponding dataset so this validation will be ignored
        if (!Constants.alphaNumericWithAlphaInitialValidation.test(transformation.name)
            && transformation.owner && Metamodel.modelOf(transformation.owner).name.toLowerCase() !== 'powerquery'
            && transformation.owner && Metamodel.modelOf(transformation.owner).name.toLowerCase() !== 'executewranglingdataflow'
            && transformation.owner && Metamodel.modelOf(transformation.owner).name.toLowerCase() !== 'powerquerysink') {
            return [
                {
                    context: transformation,
                    message: () => sanitizeAndFormatString(stringResources.DataFlowErrorAlphanumericOnly, transformation.name, transformation.owner?.name || '')
                }
            ];
        }
        return undefined;
    }

    public get descriptionToShow(): string {
        if (this.description) {
            return this.description;
        }
        if (!this._cachedDescription) {
            this._cachedDescription = this.getGeneratedDescription();
        }
        return this._cachedDescription;
    }

    // Don't use this in UI, always use distinctInputColumnRefs
    protected get inputColumns(): DataFlowColumn[] {
        const columns: DataFlowColumn[] = [];
        this.inputStreams.forEach((inputStream) => columns.push(...inputStream.producer.outputColumns(inputStream)));
        return columns;
    }

    public set distinctInputColumnRefs(columns: ColumnReference[]) {
        this._cachedDistinctInputColumns = columns;
    }

    public get distinctInputColumnRefs(): ColumnReference[] {
        if (!this._cachedDistinctInputColumns) {
            this._cachedDistinctInputColumns = this._getDistinctInputColumnRefs();
        }
        return this._cachedDistinctInputColumns;
    }

    public get distinctOutputColumnRefs(): ColumnReference[] {
        if (!this._cachedDistinctOutputColumns) {
            this._cachedDistinctOutputColumns = this._getDistinctOutputColumnRefs();
        }
        return this._cachedDistinctOutputColumns;
    }

    public get transientColumnRefs(): ColumnReference[] {
        return this.transientColumns.map((column) => new ColumnReference(column));
    }

    public get isValidDebugState(): boolean {
        return this.debugState === DebugState.Valid;
    }

    public get isInvalidDebugState(): boolean {
        return this.debugState === DebugState.Invalid;
    }

    public get isInvalidStreamDebugState(): boolean {
        return this.debugState === DebugState.InvalidStream;
    }

    private static streamModel(): IModel {
        return DataFlowStream.MODEL;
    }

    public outputColumns(stream?: DataFlowStream): DataFlowColumn[] {
        if (!this._cachedOutputColumns.get(stream)) {
            this._cachedOutputColumns.set(stream, this._outputColumns(stream));
            this.getDownTransformations().forEach((directConsumerTransformation) => {
                directConsumerTransformation._cachedDistinctInputColumns = undefined;
                directConsumerTransformation.updateColumnReferences();
            });
        }
        return this._cachedOutputColumns.get(stream);
    }

    public fromTransformation(): Transformation[] {
        const transformations: Transformation[] = [];
        this.inputStreams.forEach((stream) => transformations.push(stream.producer));

        return transformations;
    }

    public getAllUpTransformations(): Transformation[] {
        const upTransformations: Set<Transformation> = new Set();

        function addUpTransformations(transformation: Transformation): void {
            transformation.inputStreams.forEach((inputStream) => {
                if (!upTransformations.has(inputStream.producer)) {
                    upTransformations.add(inputStream.producer);
                    addUpTransformations(inputStream.producer);
                }
            });
        }

        addUpTransformations(this);
        return [...upTransformations];
    }

    public getAllOwnerTransformationsUntilBlocked(): Transformation[] {
        const upTransformations: Transformation[] = [];
        const blockingTransformationTypes = ['aggregate', 'select', 'pivot', 'unpivot'];
        const ownerTransformationTypes = ['derive'];
        this.inputStreams.forEach((inputStream) => {
            const producerMetaModelName = Metamodel.modelOf(inputStream.producer).name;
            if (!blockingTransformationTypes.includes(producerMetaModelName)) {
                if (ownerTransformationTypes.includes(producerMetaModelName)) {
                    upTransformations.push(inputStream.producer);
                }
                upTransformations.push(...inputStream.producer.getAllOwnerTransformationsUntilBlocked());
            }
        });

        return upTransformations;
    }

    public getAllDownTransformations(processedDownTransformations: Transformation[] = []): Transformation[] {
        const downTransformations: Set<Transformation> = new Set(processedDownTransformations);

        function addDownTransformations(transformation: Transformation): void {
            transformation.outputStreams.forEach((outputStream) => {
                outputStream.consumers.forEach((downTransformation) => {
                    if (!downTransformations.has(downTransformation)) {
                        downTransformations.add(downTransformation);
                        addDownTransformations(downTransformation);
                    }
                });
            });
        }

        addDownTransformations(this);
        return [...downTransformations];
    }

    public getAllPrimaryDownTransformations(): Transformation[] {
        const primaryDownTransformations: Set<Transformation> = new Set();

        function addPrimaryDownTransformations(transformation: Transformation): void {
            transformation.outputStreams.forEach((outputStream) => {
                outputStream.consumers.forEach((downTransformation) => {
                    if (!primaryDownTransformations.has(downTransformation) && outputStream === downTransformation.getFirstInputStream()) {
                        primaryDownTransformations.add(downTransformation);
                        addPrimaryDownTransformations(downTransformation);
                    }
                });
            });
        }

        addPrimaryDownTransformations(this);
        return [...primaryDownTransformations];
    }

    public getDownTransformations(): Transformation[] {
        const downTransformations: Transformation[] = [];
        this.outputStreams.forEach((outputStream) => {
            outputStream.consumers.forEach((transformation) => {
                downTransformations.push(transformation);
            });
        });
        return downTransformations;
    }

    public getFirstInputStream(): DataFlowStream {
        return this.inputStreams.length > 0 ? this.inputStreams[0] : undefined;
    }

    public attachInputStream(streamModel: DataFlowStream): void {
        this.insertInputStream(streamModel, this.inputStreams.length);
    }

    public insertInputStream(streamModel: DataFlowStream, index: number = 1): void {
        this.inputStreams.splice(index, 0, streamModel);
        streamModel.consumers.splice(index, 0, this);
    }

    public updateInputStream(oldStream: DataFlowStream, newStream: DataFlowStream): void {
        this.inputStreams = this.inputStreams.map((stream) => (stream === oldStream) ? newStream : stream);
        oldStream.removeConsumer(this);
        newStream.consumers.push(this);
    }

    public detachInputStream(streamModel: DataFlowStream): number {
        const indexToReturn = this.inputStreams.findIndex((inputStream: DataFlowStream) => inputStream.id === streamModel.id);
        streamModel.removeConsumer(this);
        this.inputStreams = this.inputStreams.filter((inputStream: DataFlowStream) => inputStream.id !== streamModel.id);
        return indexToReturn;
    }

    public getFirstOutputStream(): DataFlowStream {
        return this.outputStreams.size > 0 ? this.outputStreams.values().next().value : undefined;
    }

    public getFirstVisibleOutputStream(): DataFlowStream {
        return this.visibleOutputStreams.length > 0 ? this.visibleOutputStreams[0] : undefined;
    }

    public addOutputStream(streamName?: string): DataFlowStream {
        const stream = Owned.create<DataFlowStream>(DataFlowStream.MODEL, this);
        stream.name = streamName;
        this.outputStreams.add(stream);

        this._notifyOutputStreamChange();
        return stream;
    }

    public removeOutputStream(stream: DataFlowStream): void {
        this.outputStreams.delete(stream);
        this._notifyOutputStreamChange();
    }

    public removeAllOutputStreams(): void {
        this.outputStreams.clear();
        this._notifyOutputStreamChange();
    }

    public replaceOutputStreamsWithList(updatedStreams: string[]): void {
        const unattachedConsumers: Transformation[] = [];
        [...this.outputStreams].forEach((outputStream) => {
            const reusedStreamIndex = updatedStreams.findIndex((newStream) => newStream === outputStream.name);
            if (reusedStreamIndex >= 0) {
                updatedStreams.splice(reusedStreamIndex, 1);
            } else {
                for (let i = outputStream.consumers.length - 1; i >= 0; i--) {
                    const consumer = outputStream.consumers[i];
                    unattachedConsumers.push(consumer);
                    consumer.detachInputStream(outputStream);
                }
                outputStream.removeConsumer(this);
                this.outputStreams.delete(outputStream);
            }
        });
        updatedStreams.forEach((stream) => this.addOutputStream(stream));

        // ensure no streams are unattached
        if (!this.getFirstVisibleOutputStream()) {
            this.addOutputStream('temporary');
        }
        unattachedConsumers.forEach((consumer) => consumer.insertInputStream(this.getFirstVisibleOutputStream()));

        this._notifyOutputStreamChange();
    }

    public removeAllInputStreams(): void {
        this.inputStreams.forEach((inputStream) => {
            inputStream.removeConsumer(this);
        });
        this.inputStreams.length = 0;
    }

    public insertBetween(previousTransformation: Transformation, nextTransformation: Transformation, clickedStream: DataFlowStream): void {
        const newOutputStream = this.getFirstOutputStream();
        if (!clickedStream) {
            clickedStream = nextTransformation.getFirstInputStream();
        }
        nextTransformation.updateInputStream(clickedStream, newOutputStream);
        this.attachInputStream(clickedStream);
        this.insertBetweenInternal(previousTransformation, nextTransformation);
        this.dataflow.updateCachedDataForTransformations([this]);
    }

    public self(): Transformation {
        return this;
    }

    public updateTransformationCache(schemaValue?: string[]): void {
        this.updateInputTransformationCache();
        if (GlobalVariables.validateDataFlowAPI && schemaValue) {
            this.updateOutputTransformationCacheWithProvidedValues(schemaValue);
        } else {
            this.updateOutputTransformationCache();
        }
        this.hasHierarchicalData = this.distinctInputColumnRefs.some((col) => ([ComplexDataFlowType.TYPE, ArrayDataFlowType.TYPE] as string[]).includes(col.column.columnType));
    }

    public updateInputTransformationCache(): void {
        this._cachedDistinctInputColumns = undefined;
        this.inputCacheId = UUID.uuid4();
        if (!!this.distinctInputColumnRefs.find((columnRef) => columnRef.isAmbiguous)) {
            this.updateExpressionsByTransformationUpdate(undefined);
        }
        this.updateInputTransformationCacheInternal();
    }

    public clearCache(): void {
        this._cachedDistinctOutputColumns = undefined;
        this._cachedDistinctInputColumns = undefined;
        this._internalCachedDistinctInputColumns = undefined;
        this._internalCachedDistinctOutputColumns = undefined;
        this._cachedOutputColumns.clear();
        this._cachedColumns = undefined;
        this._cachedDescription = undefined;
        this.outputCacheId = UUID.uuid4();
    }

    public updateOutputTransformationCache(): void {
        this.clearCache();

        this.updateOutputTransformationCacheInternal();
        const outputExpressions = this.getOutputDependentExpressions();
        if (outputExpressions && outputExpressions.length) {
            outputExpressions.forEach((conditionExpression) => {
                const availableArguments = generateGrammarForColumns(this.distinctOutputColumnRefs, this.dataflow.cacheSinks, this.dataflow.parameters);
                if (GlobalVariables.validateDataFlowAPI) {
                    asyncUpdateIdentifiers(availableArguments, conditionExpression, this);
                } else {
                    updateIdentifiers(availableArguments, conditionExpression);
                }
            });
        }
    }

    public updateOutputTransformationCacheWithProvidedValues(schemaValue: string[]): void {
        const schema = DataFlowSchemaAdapterUtils.getSchemaFromString(`output(${schemaValue.join(', ')})`, this);
        // TODO: use stream specific output columns. for now not sure what this looks like
        const stream = this.getFirstOutputStream();
        const currentSchema = this._cachedOutputColumns.get(stream);
        this.clearCache();
        if (currentSchema && currentSchema.length) {
            schema.forEach((column, index, schemaArray) => {
                const existingCol = currentSchema.find((existingCol) => column.name === existingCol.name);
                // if the column in schema is expresion based and matches, replace it in the schema
                if (existingCol && existingCol.owner instanceof ExpressionBasedColumn) {
                    schemaArray[index] = existingCol;
                    existingCol.columnType = column.columnType;
                    existingCol.dataFlowColumnType = column.dataFlowColumnType;
                    existingCol.owner.dataFlowExpression.returnType = column.dataFlowColumnType;
                }
            });
        }

        if (schema.length) {
            this._cachedOutputColumns.set(stream, schema);
            this._cachedColumns = [...schema];
        } else {
            this._cachedOutputColumns.set(stream, currentSchema);
            this._cachedColumns = [...currentSchema];
        }
        this._cachedDistinctOutputColumns = this._getDistinctOutputColumnRefs();

        this.updateOutputTransformationCacheInternal();
        const outputExpressions = this.getOutputDependentExpressions();
        if (outputExpressions && outputExpressions.length) {
            outputExpressions.forEach((conditionExpression) => {
                const availableArguments = generateGrammarForColumns(this.distinctOutputColumnRefs, this.dataflow.cacheSinks, this.dataflow.parameters);
                asyncUpdateIdentifiers(availableArguments, conditionExpression, this);
            });
        }
    }

    public columnsRenamed(renamedColumns: DataFlowColumn[], updateCache: boolean = true): void {
        this._updateExpressionsOnColumnRename(this.getOutputDependentExpressions(), this.distinctOutputColumnRefs);
        this.updateParametersOnColumnRename(this.distinctInputColumnRefs);
        const visited = new Map<Transformation, boolean>();
        const stack: Transformation[] = [];
        DataFlowUtils.topologicalSort(this, stack, visited);
        const columnsToUpdate = [...renamedColumns];

        while (stack.length > 0) {
            const dependentTransformation = stack.pop();
            columnsToUpdate.concat(dependentTransformation._updateExpressionColumnsImproved(columnsToUpdate));
            if (updateCache) {
                dependentTransformation.updateTransformationCache();
            }
            dependentTransformation.updateParametersOnColumnRename(dependentTransformation.distinctInputColumnRefs);
        }
    }

    public updateParametersOnColumnRename(columnsNeedRename: ColumnReference[]): void {
        // Power query transformations will not have a dataflow as the owner
        if (!this.isPowerQueryTransformation && this.dataflow && this.dataflow.parameters) {
            this.dataflow.parameters.forEach((parameter) => {
                this._updateExpressionsOnColumnRename([parameter.defaultValueExpression], columnsNeedRename);
            });
        }
    }

    public columnRenamed(renamedColumn: DataFlowColumn, updateCache: boolean = true): void {
        this.columnsRenamed([renamedColumn], updateCache);
    }

    public argumentRenamed(named: Named, updateCache: boolean = true): void {
        [this as Transformation].concat(this.getAllDownTransformations()).forEach((dependentTransformation) => {
            if (named instanceof Parameter) {
                dependentTransformation.updateExpressionOnParameterRename(named);
            } else {
                dependentTransformation.updateExpressionOnCacheSinkRename(named as DataFlowSink);
            }
            if (updateCache) {
                dependentTransformation.updateTransformationCache();
            }
        });
    }

    public entityRenamed(): void {
        this.getAllDownTransformations().forEach((dependentTransformation) => dependentTransformation.updateExpressionsByTransformationUpdate(this));
    }

    public getAvailableInputStreams(): DataFlowStream[] {
        const streamsToIgnore: DataFlowStream[] = [];
        if (this.outputStreams.size > 0) {
            this.outputStreams.forEach((outputStream) => {
                streamsToIgnore.push(outputStream);
                streamsToIgnore.push(...outputStream.getAllDownStreams());
            });
        }
        streamsToIgnore.push(...this.inputStreams);
        return this.owner ? this.owner.getAllOutputStreams()
            .filter((stream) => streamsToIgnore.findIndex((streamToIgnore) => streamToIgnore === stream) === -1) : [];
    }

    public updateExpressionsByTransformationUpdate(transformation?: Transformation): void {
        this._updateExpressionsByTransformationUpdate(this.getExpressions(), this.distinctInputColumnRefs, transformation);
        this._updateExpressionsByTransformationUpdate(this.getOutputDependentExpressions(), this.distinctOutputColumnRefs, transformation);
    }

    // eslint-disable-next-line no-empty,@typescript-eslint/no-empty-function
    public updateColumnReferences(): void {
    }

    // eslint-disable-next-line no-empty,@typescript-eslint/no-empty-function
    public initializeTransformation(contextData: IResourceContext): void {
    }

    // eslint-disable-next-line no-empty,@typescript-eslint/no-empty-function
    public preRemoveAction(): void {
    }

    public getExpressions(): DataFlowExpression[] {
        return [];
    }

    public createTemplatableExpression(expectedType: string = StringDataFlowType.TYPE, defaultValue?: string): DataFlowExpression {
        const expression = Owned.create<DataFlowExpression>(DataFlowExpression.MODEL, this);
        expression.requiredReturnType = expectedType;
        expression.expression = defaultValue;
        expression.isTemplatable = true;
        return expression;
    }

    public createTemplatableExpressionArray<T = DataFlowExpression>(defaultValue?: T[]): DataFlowExpressionArray<T> {
        const expressionArray = Owned.create<DataFlowExpressionArray<T>>(DataFlowExpressionArray.MODEL, this);
        expressionArray.isTemplatable = true;
        if (defaultValue) {
            expressionArray.expressions = defaultValue;
        }
        return expressionArray;
    }

    public updateExpressionOnParameterRename(param: Parameter): void {
        this._updateExpressionOnParameterRename(this.getExpressions(), param);
        this._updateExpressionOnParameterRename(this.getOutputDependentExpressions(), param);
    }

    public updateExpressionOnCacheSinkRename(cacheSink: DataFlowSink): void {
        this._updateExpressionOnCacheSinkRename(this.getExpressions(), cacheSink);
        this._updateExpressionOnCacheSinkRename(this.getOutputDependentExpressions(), cacheSink);
    }

    protected getAllExpressionsForModel(): DataFlowExpression[] {
        const expressions: DataFlowExpression[] = [];
        const transformationModel = Metamodel.modelOf(this);
        transformationModel.attributes.forEach((attribute) => {
            const value = attribute.value(this);
            if (value instanceof DataFlowExpression) {
                expressions.push(value);
            }
            if (Array.isArray(value) && value[0] instanceof DataFlowExpression) {
                expressions.push(...value);
            }
        });
        return expressions.filter((exp) => !!exp.templateExpression || exp.hasUserDefinedFunction);
    }

    protected abstract getGeneratedDescription(): string;

    protected getOutputDependentExpressions(): DataFlowExpression[] {
        return this.partitionConditions;
    }

    protected _updateExpressionColumnIdentifiers(dataflowColumns: ExpressionBasedColumnBase[], dataflowArgs: IDataFlowArguments,
                                                 dataflowTemplateArgs: IDataFlowArguments, typeDefinitionArguments?: IDataFlowArguments): void {
        dataflowColumns.forEach((dataflowColumn) => {
            // Not updating the identifier for the pattern columns, for validation we need it.
            if (dataflowColumn instanceof ExpressionBasedColumn) {
                if (dataflowColumn.dataFlowExpression) {
                    let dataflowArgsToUse = dataflowArgs;
                    if (dataflowColumn.column.isTransient) {
                        const endIndex = this.transientExpressionColumns.findIndex((expressionColumn) => expressionColumn === dataflowColumn);
                        const usableTransientColumns = dataflowArgs.dataFlowTransientColumns.slice(0, endIndex);
                        dataflowArgsToUse = {
                            dataFlowFunctions: dataflowArgs.dataFlowFunctions,
                            dataFlowColumns: dataflowArgs.dataFlowColumns,
                            dataFlowTransientColumns: usableTransientColumns,
                            dataFlowParameters: dataflowArgs.dataFlowParameters,
                            dataFlowCacheSinks: dataflowArgs.dataFlowCacheSinks
                        };
                    }
                    if (dataflowColumn.fixedReturnTypeExpression.expression) {
                        if (GlobalVariables.validateDataFlowAPI) {
                            asyncUpdateIdentifiers(typeDefinitionArguments, dataflowColumn.fixedReturnTypeExpression, this, true);
                        } else {
                            updateIdentifiers(typeDefinitionArguments, dataflowColumn.fixedReturnTypeExpression, true);
                        }
                    }
                    if (GlobalVariables.validateDataFlowAPI) {
                        asyncUpdateIdentifiers(dataflowArgsToUse, dataflowColumn.dataFlowExpression, this);
                    } else {
                        updateIdentifiers(dataflowArgsToUse, dataflowColumn.dataFlowExpression);
                    }
                    if (dataflowColumn.column.isTransient) {
                        dataflowColumn.column.dataFlowColumnType = dataflowColumn.expressionReturnType;
                    }
                }
            } else if (dataflowColumn instanceof ExpressionBasedColumnPattern) {
                if (GlobalVariables.validateDataFlowAPI) {
                    asyncUpdateIdentifiers(dataflowTemplateArgs, dataflowColumn.templateCondition, this);
                    asyncUpdateIdentifiers(dataflowArgs, dataflowColumn.hierarchyLevelExpression, this);
                } else {
                    updateIdentifiers(dataflowTemplateArgs, dataflowColumn.templateCondition);
                    updateIdentifiers(dataflowArgs, dataflowColumn.hierarchyLevelExpression);
                }
                ColumnExpressionProcessor.calculateExpressionPatternBasedOnType([dataflowColumn], this.distinctInputColumnRefs);

                const dataFlowPatternParameterColumn = Owned.create<DataFlowColumn>(Metamodel.modelFor(DataFlowColumn), this);
                dataFlowPatternParameterColumn.name = dataFlowPatternParameter;
                dataFlowPatternParameterColumn.dataFlowColumnType = DataFlowTypesUtils.createType(dataflowColumn.basedColumnDataType ?
                    dataflowColumn.basedColumnDataType.typeString : 'any');
                const dataFlowPatternParameterColumnArgs = convertColumnRefToDataFlowColumn([new ColumnReference(dataFlowPatternParameterColumn, undefined, undefined, true)]);

                const dataFlowMetaParameterColumnArgs: DataFlowColumnArgument[] = [];

                const dataFlowPositionParameterColumn = Owned.create<DataFlowColumn>(Metamodel.modelFor(DataFlowColumn), this);
                dataFlowPositionParameterColumn.name = dataFlowPositionParameter;
                dataFlowPositionParameterColumn.dataFlowColumnType = DataFlowTypesUtils.createType(IntegerDataFlowType.TYPE);
                dataFlowMetaParameterColumnArgs.push(...convertColumnRefToDataFlowColumn([new ColumnReference(dataFlowPositionParameterColumn, undefined, undefined, true)]));

                const dataFlowHierarchicalPathParameterColumn = Owned.create<DataFlowColumn>(Metamodel.modelFor(DataFlowColumn), this);
                dataFlowHierarchicalPathParameterColumn.name = dataFlowHierarchyPathParameter;
                dataFlowHierarchicalPathParameterColumn.dataFlowColumnType = DataFlowTypesUtils.createType(dataflowColumn.basedColumnDataType ?
                    dataflowColumn.basedColumnDataType.typeString : 'any');
                dataFlowMetaParameterColumnArgs.push(...convertColumnRefToDataFlowColumn([new ColumnReference(dataFlowHierarchicalPathParameterColumn, undefined, undefined, true)]));

                const dataFlowRegexParameterColumnArgs = convertColumnRefToDataFlowColumn(
                    PatternExpressionColumnsUtils.createCaptureGroupInputColumnRefs(this, dataflowColumn.regexString));

                const patternArgs: IDataFlowArguments = {
                    dataFlowFunctions: dataflowArgs.dataFlowFunctions,
                    dataFlowColumns: [...dataFlowPatternParameterColumnArgs, ...dataFlowMetaParameterColumnArgs, ...dataflowArgs.dataFlowColumns, ...dataFlowRegexParameterColumnArgs],
                    dataFlowParameters: (dataflowArgs.dataFlowParameters ?
                        [].concat(...dataflowArgs.dataFlowParameters) : []),
                    dataFlowTransientColumns: dataflowArgs.dataFlowTransientColumns,
                    dataFlowCacheSinks: dataflowArgs.dataFlowCacheSinks
                };

                const patternColumnNameArgs = PatternExpressionColumnsUtils.getAvailableArgumentsForColumnNameExpression(this.dataflow.parameters, this.dataflow.cacheSinks, this,
                    false, false, dataflowColumn.regexString);

                dataflowColumn.columns.forEach((column) => {
                    if (GlobalVariables.validateDataFlowAPI) {
                        asyncUpdateIdentifiers(patternColumnNameArgs, column.columnNameExpression, this);
                        asyncUpdateIdentifiers(patternArgs, column.dataFlowExpression, this);
                    } else {
                        updateIdentifiers(patternColumnNameArgs, column.columnNameExpression);
                        updateIdentifiers(patternArgs, column.dataFlowExpression);
                    }
                    column.column.dataFlowColumnType = column.dataFlowExpression.returnType;
                });
            } else if (dataflowColumn instanceof DataFlowExpressionBasedMapColumn) {
                if (dataflowColumn.templateCondition && dataflowColumn.templateCondition.expression) {
                    if (GlobalVariables.validateDataFlowAPI) {
                        asyncUpdateIdentifiers(dataflowTemplateArgs, dataflowColumn.templateCondition, this);
                    } else {
                        updateIdentifiers(dataflowTemplateArgs, dataflowColumn.templateCondition);
                    }
                }
                if (dataflowColumn.dataFlowExpression && dataflowColumn.dataFlowExpression.expression) {
                    if (GlobalVariables.validateDataFlowAPI) {
                        asyncUpdateIdentifiers(dataflowArgs, dataflowColumn.dataFlowExpression, this);
                    } else {
                        updateIdentifiers(dataflowArgs, dataflowColumn.dataFlowExpression);
                    }
                }
            }
        });
    }

    protected _outputColumns(stream?: DataFlowStream): DataFlowColumn[] {
        return this.inputColumns;
    }

    protected updateInputTransformationCacheInternal(): void {
        this.getAllExpressionsForModel().forEach((expression) => {
            if (GlobalVariables.validateDataFlowAPI) {
                asyncUpdateIdentifiers(generateGrammarForColumns([], this.dataflow.cacheSinks, this.dataflow.parameters), expression, this);
            } else {
                updateIdentifiers(generateGrammarForColumns([], this.dataflow.cacheSinks, this.dataflow.parameters), expression);
            }
        });
    }

    // eslint-disable-next-line no-empty,@typescript-eslint/no-empty-function
    protected updateOutputTransformationCacheInternal(): void {
    }

    protected _notifyOutputStreamChange(): void {
        // eslint-disable-next-line no-restricted-syntax
        if (Application.injector == null) return; // Fabric migration of Dataflows: Application.injector may be uninitialized; skip this broadcast if so.
        // eslint-disable-next-line no-restricted-syntax
        const msgSvc = Application.injector.get(MessagingService);
        msgSvc.broadcast(MessagingServiceChannel.DataFlowOutputStreamUpdate, new Message(MessageKind.Entity, this));
    }

    protected _updateExpressionArgument(expression: DataFlowExpression, oldArgument: BasicExpression, newArgument: string): void {
        // it would fail few cases as the .start .stop offset might include annotation length
        let newExpression = expression.expression.substring(0, oldArgument.start);
        newExpression += newArgument;
        newExpression += expression.expression.substring(oldArgument.stop + 1);
        oldArgument.stop = oldArgument.start + newArgument.length - 1;
        expression.expression = newExpression;
    }

    // eslint-disable-next-line no-empty, @typescript-eslint/no-empty-function
    protected insertBetweenInternal(previousTransformation: Transformation, nextTransformation: Transformation): void {
    }

    protected getOutputColumnStream(column: DataFlowColumn): DataFlowStream {
        return this.getFirstOutputStream();
    }

    private _getDistinctInputColumnRefs(): ColumnReference[] {
        const dInputColumns: ColumnReference[] = [];
        this._internalCachedDistinctInputColumns = this._internalCachedDistinctInputColumns || [];

        this.inputStreams.forEach((inputStream) => {
            inputStream.producer.outputColumns(inputStream).forEach((column) => {
                if (column) {
                    const existingColumnRef = this._internalCachedDistinctInputColumns.find((internalCol) => internalCol.column === column);
                    const columnRef = existingColumnRef || new ColumnReference(column, inputStream, column.ownerTransformation.getOutputColumnStream(column));
                    // make sure the input stream is updated in cases where a transformation is deleted.
                    columnRef.inputStream = inputStream;
                    // Although column names are case sensitive, but for better usagability,
                    // in case columns are having same name, show fullyqualified names.
                    const existColumn = dInputColumns.find((dColumn) => stringsAreEqualCaseInsensitive(dColumn.column.name, column.name));
                    columnRef.isAmbiguous = false;
                    if (existColumn) {
                        existColumn.isAmbiguous = true;
                        columnRef.isAmbiguous = true;
                    }
                    dInputColumns.push(columnRef);
                }
            });
        });
        this._internalCachedDistinctInputColumns = dInputColumns;

        return dInputColumns;
    }

    private _getDistinctOutputColumnRefs(): ColumnReference[] {
        const dOutputColumns: ColumnReference[] = [];
        this._internalCachedDistinctOutputColumns = this._internalCachedDistinctOutputColumns || [];

        this.outputColumns().forEach((column) => {
            const existingColumnRef = this._internalCachedDistinctOutputColumns.find((internalCol) => internalCol.column === column);
            const columnRef = existingColumnRef || new ColumnReference(column);
            // Although column names are case sensitive, but for better usagability,
            // in case columns are having same name, show fullyqualified names.
            const existColumn = dOutputColumns.find((dColumn) => stringsAreEqualCaseInsensitive(dColumn.column.name, column.name));
            columnRef.isAmbiguous = false;
            if (existColumn) {
                existColumn.isAmbiguous = true;
                columnRef.isAmbiguous = true;
            }
            dOutputColumns.push(columnRef);
        });
        this._internalCachedDistinctOutputColumns = dOutputColumns;

        return dOutputColumns;
    }

    private _updateExpressionsByTransformationUpdate(expressions: DataFlowExpression[], distinctColumnRefs: ColumnReference[], transformation?: Transformation): void {
        expressions.forEach((dataFlowExpression) => {
            let positionShift = 0;
            const expressions = getSortedExpressionsInColumnArguments(dataFlowExpression.argumentsUsed.dataFlowColumns);
            expressions.forEach((usedExpression) => {
                usedExpression.expression.start += positionShift;
                usedExpression.expression.stop += positionShift;

                if (!transformation || usedExpression.column.column.dataFlowColumn.ownerTransformation === transformation) {
                    const columnRef = distinctColumnRefs.find((cRef) => cRef.column === usedExpression.column.column.dataFlowColumn);
                    if (columnRef && columnRef.isAmbiguous) {
                        const newArgument = columnRef.serializableName;
                        positionShift += newArgument.length - (usedExpression.expression.stop - usedExpression.expression.start + 1);
                        // here is an example that would keep failing with offsets assertion of annotations
                        this._updateExpressionArgument(dataFlowExpression, usedExpression.expression, newArgument);
                    }
                }
            });
        });
    }

    private _updateExpressionColumnsImproved(renamedColumns: DataFlowColumn[]): DataFlowColumn[] {
        // Do NOT remove this line, this assignment calls the get function of this.columns
        // which calls the column processor, that needs to happen before the logic below.
        const currentColumns = this.columns;

        // Retest for ambiguity after name change
        this.distinctInputColumnRefs.forEach((columnRef) => {
            columnRef.isAmbiguous = !!this.distinctInputColumnRefs.find((otherRef) => otherRef.column.name === columnRef.column.name && otherRef.column !== columnRef.column);
        });

        this._updateExpressionsOnColumnRename(this.getExpressions(), this.distinctInputColumnRefs);
        this._updateExpressionsOnColumnRename(this.getOutputDependentExpressions(), this.distinctOutputColumnRefs);

        // handle overwritten columns
        const allReferencingColumns: DataFlowColumn[] = [];
        renamedColumns.forEach((renamedColumn) => {
            const referencingColumns = currentColumns.filter((column) => column.basedOnArguments.includes(renamedColumn));
            allReferencingColumns.concat(referencingColumns);
            referencingColumns.forEach((column) => {
                if (column.nameDependsOnBasedColumn && !column.columnPattern) {
                    column.name = renamedColumn.name;
                }
            });
        });
        return allReferencingColumns;
    }

    private _updateExpressionsOnColumnRename(expressions: DataFlowExpression[], columnsNeedRename: ColumnReference[]): void {
        expressions.forEach((dataFlowExpression) => {
            let positionShift = 0;
            // As we use DFS, there's chance when renaming the transform's columns, not all upstreams are updated, it will be renamed again
            // Need to sort the expressions by start index, so our position shift is always incremental and global for the columns that still need rename.
            const expressions = getSortedExpressionsInColumnArguments(dataFlowExpression.argumentsUsed.dataFlowColumns);

            // There are some issues when multiple of the same column expression (coming from joins) is being replaced in the subsequent expressions, so removing duplicates here
            for (let i = expressions.length; i--; i >= 0) {
                if (expressions.find((exp, idx) => idx !== i && exp.expression.start === expressions[i].expression.start && exp.expression.returnValue === expressions[i].expression.returnValue)) {
                    expressions.splice(i, 1);
                }
            }
            expressions.forEach((usedExpression) => {
                // Some expressions have the start/stop in the expression value rather than the expression itself
                const expressionWithStartStop = usedExpression.expression.start !== undefined && usedExpression.expression.stop !== undefined ? usedExpression.expression
                    : usedExpression.expression.value || usedExpression.expression;
                expressionWithStartStop.start += positionShift;
                expressionWithStartStop.stop += positionShift;
                const usedCol = columnsNeedRename.find((colNeedRename) => usedExpression.column.column.dataFlowColumn === colNeedRename.column);
                if (usedCol) {
                    positionShift += usedCol.serializableName.length - (expressionWithStartStop.stop - expressionWithStartStop.start + 1);
                    this._updateExpressionArgument(dataFlowExpression, expressionWithStartStop, usedCol.serializableName);
                }
            });
        });
    }

    private _updateExpressionOnParameterRename(expressions: DataFlowExpression[], updatedParam: Parameter): void {
        expressions.forEach((dataFlowExpression) => {
            dataFlowExpression.argumentsUsed.dataFlowParameters.forEach((usedParam) => {
                // eslint-disable-next-line no-template-curly-in-string
                const expressionFormatFn = (name: string) => `$${name}`;
                this._checkUsedIdentifierForUpdate(usedParam, updatedParam, usedParam.parameter.dataFlowParameter, dataFlowExpression, expressionFormatFn);
            });
        });
    }

    private _updateExpressionOnCacheSinkRename(expressions: DataFlowExpression[], updatedCacheSink: DataFlowSink): void {
        expressions.forEach((dataFlowExpression) => {
            dataFlowExpression.argumentsUsed.dataFlowCacheSinks.forEach((usedCacheSink) => {
                const expressionFormatFn = (name: string, exp: string) => {
                    const functionExp = exp.substr(exp.indexOf('#'));
                    return `${name}${functionExp}`;
                };
                this._checkUsedIdentifierForUpdate(usedCacheSink, updatedCacheSink, usedCacheSink.cacheSink.cacheSink, dataFlowExpression, expressionFormatFn);
            });
        });
    }

    private _checkUsedIdentifierForUpdate(usedIdentifier: IUsedIdentifier, updatedNamed: Named, usedIdentifierNamed: Named,
                                          dataFlowExpression: DataFlowExpression, nameTemplateFn: (name: string, exp?: string) => string): void {
        let positionShift = 0;
        usedIdentifier.expression.forEach((usedExpression) => {
            usedExpression.start += positionShift;
            usedExpression.stop += positionShift;
            if (usedIdentifierNamed === updatedNamed) {
                const newArgument = nameTemplateFn(updatedNamed.name, usedExpression.text);
                positionShift += newArgument.length - (usedExpression.stop - usedExpression.start + 1);
                this._updateExpressionArgument(dataFlowExpression, usedExpression, newArgument);
            }
        });
    }
}
